<script setup lang="ts">
import {
  DefinitionDto,
  DefinitionParameterDto,
  DefinitionParameterValueDto,
  DefinitionParameterValueSetDto,
  EntityOrigin,
  NumericQuantityDto,
} from '@/api/generator';
import ContextMenu from '@/components/ContextMenu/ContextMenu.vue';
import { ContextMenuAction } from '@/components/ContextMenu/types.ts';
import { globalContextMenuHeaderItemDefinition } from '@/components/ContextMenu/utils.ts';
import { useFormMode } from '@/components/forms/useFormMode.ts';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils.ts';
import { deleteIcon, edit } from '@/components/icons/icons.ts';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import { DataTableHeader, OverviewTableProps } from '@/components/OverviewTable/types.ts';
import { useProtocolsStore } from '@/stores/protocolsStore.ts';
import { useUnitsStore } from '@/stores/unitsStore.ts';
import { translateEntityProperty, truthy } from '@/utils/helper.ts';
import { toDisplayValue } from '@/utils/numberUtils.ts';
import { useTranslation } from 'i18next-vue';
import { NIL } from 'uuid';
import { useFieldValue, useFormValues } from 'vee-validate';
import { computed, onMounted, toRef } from 'vue';

type DefinitionParameterSetsTableProps = {
  name: string;
  allowExtend: boolean;
};
type Quantity = NumericQuantityDto;

const props = defineProps<DefinitionParameterSetsTableProps>();
const definition = useFieldValue<DefinitionDto>(toRef(() => props.name));

const values = useFormValues<Quantity>();

type DefinitionParameterSetsTableEmits = {
  edit: [string];
  delete: [string];
};
const emit = defineEmits<DefinitionParameterSetsTableEmits>();

type DefinitionParameterValueSetItem = {
  setIdentifier: DefinitionParameterValueSetDto['setIdentifier'];
  parameter: DefinitionParameterDto;
  value: DefinitionParameterValueDto;
};

const { t } = useTranslation();
const mode = useFormMode();
const unitsStore = useUnitsStore();
const protocolsStore = useProtocolsStore();

onMounted(async () => {
  await unitsStore.initialize();
  await protocolsStore.initialize();
});

const headers = computed<OverviewTableProps<DefinitionParameterValueSetDto, string>['headers']>(
  () => {
    const paramHeaders: DataTableHeader<string>[] =
      definition.value.definitionParameters?.map((v) => ({
        title: translateEntityProperty('name', v) ?? v.name,
        key: v.name,
        sortable: false,
        align: 'end',
      })) || [];
    return [
      ...paramHeaders,
      {
        title: t('DefinitionParameterProtocol_one'),
        key: 'protocolId',
        sortable: false,
      },
      mode.value === 'edit' || mode.value === 'add'
        ? { ...globalContextMenuHeaderItemDefinition, width: '24px' }
        : undefined,
    ].filter(truthy);
  },
);
const items = computed(() => {
  if (
    definition.value.definitionParameterValueSets &&
    definition.value.definitionParameterValueSets.length
  ) {
    return [
      ...definition.value.definitionParameterValueSets.map((s) => {
        const rowObject: Record<string, any> = {};
        definition.value.definitionParameters?.forEach((p, index) => {
          rowObject[p.name] = {
            setIdentifier: s.setIdentifier,
            parameter: p,
            value: s.values?.[index],
          };
        });
        rowObject['protocolId'] = s.protocolId;
        return rowObject;
      }),
    ];
  } else return [];
});
function isDefinitionParameterValueSetItem(
  item: Record<string, any>,
): item is Record<string, DefinitionParameterValueSetItem> {
  const setIdentifier = Object.values(item)[0].setIdentifier;
  return setIdentifier !== undefined;
}
function getContextMenuActions(item: Record<string, any>): ContextMenuAction[] {
  if (!isDefinitionParameterValueSetItem(item)) {
    return [];
  }
  const setIdentifier = Object.values(item)[0].setIdentifier;
  const set = definition.value.definitionParameterValueSets?.find(
    (s) => s.setIdentifier === setIdentifier,
  );
  const isPredefined = set?.origin === EntityOrigin.Predefined;

  return [
    {
      disabled: isPredefined,
      tooltip: isPredefined ? t('EditDefinitionParameterSetDisabledReasonTooltipMsg') : undefined,
      icon: edit,
      text: t('Edit'),
      callback: () => {
        emit('edit', setIdentifier);
      },
    },
    {
      disabled: isPredefined || (values.value.isReferenced && set?.id !== NIL),
      tooltip:
        isPredefined || (values.value.isReferenced && set?.id !== NIL)
          ? t('DeleteDefinitionParameterSetDisabledReasonTooltipMsg')
          : undefined,

      icon: deleteIcon,
      text: t('Delete'),
      callback: async () => {
        emit('delete', setIdentifier);
      },
    },
  ];
}
</script>

<template>
  <OverviewTable
    :headers="headers"
    :items="items"
    itemKey="name"
    :showItemsCount="false"
    :zebra="true"
    :noSpacing="true"
    data-testid="DataTable_DefinitionParametersSets"
  >
    <template
      v-for="header in headers
        ?.filter((h) => h.key !== 'contextMenu')
        .filter((h) => h.key !== 'protocolId')"
      :key="header.key"
      #[`item.${header.key}`]="{ item }"
    >
      {{
        toDisplayValue(item[header.key]?.value.value, {
          valueType: item[header.key]?.parameter.valueType,
          fractionDigits: item[header.key]?.parameter.decimalPlaces,
          unitAbbreviation: unitsStore.getById(item[header.key]?.parameter.unitId)?.abbreviation,
        })
      }}
    </template>
    <template #item.protocolId="{ item }">
      {{ protocolsStore.getById(item.protocolId)?.name ?? EMPTY_FIELD_SIGN }}
    </template>
    <template #item.contextMenu="{ item }">
      <ContextMenu :actions="getContextMenuActions(item)" />
    </template>
  </OverviewTable>
</template>
